import { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { themes } from '../themes/themeConfig';

// Create the Theme Context
const ThemeContext = createContext(undefined);

// LocalStorage key for theme persistence
const THEME_STORAGE_KEY = 'homeThemePreference';
const DEFAULT_THEME = 'darkMidnight';

/**
 * Safely reads theme preference from localStorage with error handling
 * @returns {string|null} The saved theme ID or null if read fails
 */
const loadThemeFromStorage = () => {
  try {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);

    // Validate that the saved theme exists in our themes configuration
    if (savedTheme && themes[savedTheme]) {
      console.log(`Loaded theme preference: ${savedTheme}`);
      return savedTheme;
    } else if (savedTheme) {
      // Invalid theme ID was stored
      console.warn(`Invalid theme ID stored in localStorage: ${savedTheme}. Falling back to default theme.`);
      return null;
    }
  } catch (error) {
    console.error('Failed to load theme preference from localStorage:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
    });
  }

  return null;
};

/**
 * Safely saves theme preference to localStorage with error handling
 * @param {string} themeId - The theme ID to save
 * @returns {boolean} True if save was successful, false otherwise
 */
const saveThemeToStorage = (themeId) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, themeId);
    console.log(`Saved theme preference: ${themeId}`);
    return true;
  } catch (error) {
    console.error('Failed to save theme preference to localStorage:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
    });

    // Check for quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.error('LocalStorage quota exceeded. Theme preference will not persist.');
    }

    return false;
  }
};

/**
 * ThemeProvider component that manages theme state and persistence
 * Provides theme context to all child components
 */
export const ThemeProvider = ({ children }) => {
  // Initialize theme state with value from localStorage or default
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = loadThemeFromStorage();
    return savedTheme || DEFAULT_THEME;
  });

  /**
   * Set theme function that updates state and persists to localStorage
   * @param {string} themeId - The ID of the theme to activate
   */
  const setTheme = (themeId) => {
    // Validate theme ID exists
    if (!themes[themeId]) {
      console.error(`Invalid theme ID: ${themeId}. Falling back to default theme.`);
      themeId = DEFAULT_THEME;
    }

    // Update state first to ensure UI updates even if localStorage fails
    setCurrentTheme(themeId);

    // Attempt to persist to localStorage (non-blocking)
    saveThemeToStorage(themeId);
  };

  // Memoize theme styles to prevent unnecessary re-renders
  // Includes fallback to default theme if current theme is somehow invalid
  const themeStyles = useMemo(() => {
    try {
      const styles = themes[currentTheme]?.styles;

      if (!styles) {
        console.error(`Theme styles not found for: ${currentTheme}. Using default theme styles.`);
        return themes[DEFAULT_THEME].styles;
      }

      return styles;
    } catch (error) {
      console.error('Error accessing theme styles:', error);
      // Ultimate fallback to ensure application remains functional
      return themes[DEFAULT_THEME].styles;
    }
  }, [currentTheme]);

  // Apply AppBar color globally across all pages
  useEffect(() => {
    const appBar = document.querySelector('header.MuiAppBar-root');

    if (appBar && themeStyles.appBar?.background) {
      appBar.style.background = themeStyles.appBar.background;
      appBar.style.transition = 'background 0.3s ease';
    }
  }, [themeStyles]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    currentTheme,
    setTheme,
    themeStyles,
  }), [currentTheme, themeStyles]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Custom hook to consume theme context
 * @returns {Object} Theme context value with currentTheme, setTheme, and themeStyles
 * @throws {Error} If used outside of ThemeProvider
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    console.error('useTheme hook was called outside of ThemeProvider. Ensure the component is wrapped with ThemeProvider.');
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
